import React, { useEffect, useState, useRef } from "react";
import "../css/main/Auth.css";
import { apiCall, BASE_URL, hasAuthCookie } from "./ApiCall";
import { LoginProps, Client, Provider, createToastMessage, ToastMessage } from "../interFaces/interfaces";
import ToastPopup from "../popups/ToastPopup";
import * as FcIcons from "react-icons/fc";
import * as SiIcons from "react-icons/si";
import * as FaIcons from "react-icons/fa";
import { IconType } from "react-icons";
import axios from "axios";
const Login: React.FC<LoginProps> = ({ setIsAuthenticated }) => {
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [isButtonClicked, setIsButtonClicked] = useState<boolean>(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState<boolean>(false);
  const [showLoginForm, setShowLoginForm] = useState<boolean>(false);
  const [showOtpForm, setShowOtpForm] = useState<boolean>(false);
  const [, setOtpId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [, setIsSamlProvider] = useState<boolean>(false);
  const [isFetchingClientDetails, setIsFetchingClientDetails] = useState<boolean>(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const clientId = localStorage.getItem("clientId");
  const hostname = window.location.hostname;
  const isDevOtpAllowed = hostname === "dev.auditzen.ai" || hostname === "qa.auditzen.ai" || hostname === "localhost";
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (message) {
      timer = setTimeout(() => { setMessage(null) }, 2500);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [message]);
  useEffect(() => {
    const fetchClientsOrInfo = () => {
      setLoading(true);
      try {
        const hostname = window.location.hostname;
        const subdomain = hostname === "localhost" ? "localhost" : hostname.split(".")[0];
        if (["localhost", "dev.auditzen.ai", "qa.auditzen.ai"].includes(hostname)) {
          apiCall("get", `/api/client-apis/names/all`)
            .then((responseData: Client[]) => {
              if (Array.isArray(responseData) && responseData.length > 0) {
                setClients(responseData);
                setShowClientDropdown(true);
              } else {
                setMessage(createToastMessage("No clients found. Please contact support.", "error", 3000));
              }
            })
            .catch(() => {
              setMessage(createToastMessage("Failed to fetch client info. Please try again.", "error", 3000));
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          apiCall("get", `/api/client-apis/info?subdomain=${subdomain}`)
            .then((subdomainData: Client[]) => {
              if (Array.isArray(subdomainData) && subdomainData.length > 0) {
                const client = subdomainData[0];
                localStorage.setItem("clientId", client.clientId);
                if (client.providers && client.providers.length > 0) {
                  localStorage.setItem("providerId", client.providers[0].providerId);
                }
                setSelectedClient(client);
                setProviders(client.providers.filter((p) => p.enabled));
                setShowLoginForm(true);
              } else {
                setMessage(createToastMessage("Client not found. Please check your URL.", "error", 3000));
              }
            })
            .catch(() => {
              setMessage(createToastMessage("Failed to fetch client info. Please try again.", "error", 3000));
            })
            .finally(() => {
              setLoading(false);
            });
        }
      } catch (error) {
        setMessage(createToastMessage("Failed to fetch client info. Please try again.", "error", 3000));
        setLoading(false);
      }
    };
    fetchClientsOrInfo();
  }, []);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);
  const handleClientSelect = (selectedClientId: string) => {
    const selectedClientObj = clients.find((c) => c.clientId === selectedClientId);
    if (selectedClientObj) {
      setIsFetchingClientDetails(true);
      apiCall("get", `/api/client-apis/names/${selectedClientId}`)
        .then((clientDetails: Client) => {
          if (clientDetails) {
            localStorage.setItem("clientId", clientDetails.clientId);
            if (clientDetails.providers && clientDetails.providers.length > 0) {
              localStorage.setItem("providerId", clientDetails.providers[0].providerId);
            }
            setSelectedClient(clientDetails);
            setProviders(clientDetails.providers ? clientDetails.providers.filter((p) => p.enabled) : []);
            setShowLoginForm(true);
            setShowClientDropdown(false);
          } else {
            setMessage(createToastMessage("Failed to fetch client details. Please try again.", "error", 3000));
          }
        })
        .catch(() => {
          setMessage(createToastMessage("Failed to fetch client details. Please try again.", "error", 3000));
        })
        .finally(() => {
          setIsFetchingClientDetails(false);
        });
    } else {
      setMessage(createToastMessage("Selected client not found.", "error", 3000));
    }
  };
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const handleEmailLogin = () => {
    if (!email) {
      setMessage(createToastMessage("Please enter your email address.", "error", 3000));
      return;
    }
    if (!validateEmail(email)) {
      setMessage(createToastMessage("Please enter a valid email address.", "error", 3000));
      return;
    }
    if (!clientId) {
      setMessage(createToastMessage("Client information not found. Please select a client.", "error", 3000));
      return;
    }
    setIsButtonClicked(true);
    setLoading(true);
    const requestData = { email: email.trim().toLowerCase(), clientId: clientId };
    axios.post(`${BASE_URL}/api/client-apis/email-login`, requestData, { headers: { "Content-Type": "application/json" }, withCredentials: true })
      .then((response) => {
        const data = response.data;
        if (data.success) {
          if (data.otpRequired) {
            setOtpId(data.otpId);
            setShowOtpForm(true);
            setResendTimer(120);
            setMessage(createToastMessage("OTP sent to your email. Please check your inbox.", "success", 3000));
          } else {
            handleSuccessfulLogin();
          }
        } else {
          setMessage(createToastMessage(data.message || "Login failed. Please try again.", "error", 3000));
        }
      })
      .catch((error) => {
        if (error.response?.data?.message) {
          setMessage(createToastMessage(error.response.data.message, "error", 3000));
        } else {
          setMessage(createToastMessage("Login failed. Please try again.", "error", 3000));
        }
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => setIsButtonClicked(false), 2000);
      });
  };
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && otp.every(digit => digit !== "")) {
      handleOtpVerification();
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').slice(0, 6);
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
      const focusIndex = Math.min(newOtp.length, 5);
      otpRefs.current[focusIndex]?.focus();
    }
  };
  const handleOtpVerification = () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setMessage(createToastMessage("Please enter a valid 6-digit OTP.", "error", 3000));
      return;
    }
    if (!clientId) {
      setMessage(createToastMessage("Client information not found.", "error", 3000));
      return;
    }
    setLoading(true);
    const requestData = { email: email.trim().toLowerCase(), clientId: clientId, otp: otpString };
    axios.post(`${BASE_URL}/api/client-apis/verify-otp`, requestData, { headers: { "Content-Type": "application/json" }, withCredentials: true })
      .then((response) => {
        const data = response.data;
        if (data.success) {
          handleSuccessfulLogin();
        } else {
          setMessage(createToastMessage(data.message || "Invalid OTP. Please try again.", "error", 3000));
        }
      })
      .catch((error) => {
        if (error.response?.data?.message) {
          setMessage(createToastMessage(error.response.data.message, "error", 3000));
        } else {
          setMessage(createToastMessage("OTP verification failed. Please try again.", "error", 3000));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleSuccessfulLogin = () => {
    localStorage.setItem("email", email.trim().toLowerCase());
    localStorage.setItem("authEvent", JSON.stringify({ type: "LOGIN" }));
    setTimeout(() => {
      const hasAuth = hasAuthCookie();
      if (hasAuth) {
        setMessage(createToastMessage("Login successful! Redirecting...", "success", 3000));
        if (setIsAuthenticated) setIsAuthenticated(true);
        window.location.href = "/dashboard";
      } else {
        setMessage(createToastMessage("Authentication failed. Please try again.", "error", 3000));
      }
    }, 1000);
  };
  const handleResendOtp = () => {
    if (resendTimer > 0) return;
    if (!clientId) {
      setMessage(createToastMessage("Client information not found.", "error", 3000));
      return;
    }
    setLoading(true);
    const requestData = { email: email.trim().toLowerCase(), clientId: clientId };
    axios.post(`${BASE_URL}/api/client-apis/resend-otp`, requestData, { headers: { "Content-Type": "application/json" }, withCredentials: true })
      .then((response) => {
        const data = response.data;
        if (data.success) {
          setOtpId(data.otpId);
          setResendTimer(60);
          setMessage(createToastMessage("New OTP sent to your email.", "success", 3000));
        } else {
          setMessage(createToastMessage(data.message || "Failed to resend OTP. Please try again.", "error", 3000));
        }
      })
      .catch((error) => {
        if (error.response?.data?.message) {
          setMessage(createToastMessage(error.response.data.message, "error", 3000));
        } else {
          setMessage(createToastMessage("Failed to resend OTP. Please try again.", "error", 3000));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleProviderLogin = async (providerId: string, providerName: string) => {
    setLoading(true);
    try {
      localStorage.setItem("providerId", providerId);
      const isSaml = providerName.toLowerCase().includes("saml") || providerName.toLowerCase().includes("azure") || providerName.toLowerCase().includes("adfs");
      setIsSamlProvider(isSaml);
      apiCall("get", `/api/client-apis/generate-url?clientId=${clientId}&providerId=${providerId}`)
        .then((data) => {
          if (data?.loginUrl) {
            window.location.href = data.loginUrl;
          } else {
            setMessage(createToastMessage(`Failed to generate ${isSaml ? 'SAML' : 'OAuth'} login URL. Please try again.`, "error", 3000));
          }
        })
        .catch(() => {
          setMessage(createToastMessage(`${isSaml ? 'SAML' : 'OAuth'} login generation failed. Please try again.`, "error", 3000));
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      setMessage(createToastMessage("Unexpected error occurred.", "error", 3000));
      setLoading(false);
    }
  };
  const getProviderIcon = (providerName: string) => {
    if (!providerName) return <FcIcons.FcDocument size={28} className="providerIcon" />;
    const providerLower = providerName.toLowerCase();
    if (providerLower.includes("saml") || providerLower.includes("azure")) {
      return <FcIcons.FcBusinessContact size={28} className="providerIcon" />;
    }
    const pascalCaseName = providerName
      .replace(/[^a-zA-Z0-9]/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("");
    const iconSets: Record<string, Record<string, IconType>> = { Si: SiIcons as unknown as Record<string, IconType>, Fc: FcIcons as unknown as Record<string, IconType>, Fa: FaIcons as unknown as Record<string, IconType> };
    for (const prefix of Object.keys(iconSets)) {
      const Icon = iconSets[prefix][`${prefix}${pascalCaseName}`];
      if (Icon) return <Icon size={28} className="providerIcon" />;
    }
    return <FcIcons.FcDocument size={28} className="providerIcon" />;
  };
  const goBackToEmail = () => {
    setShowOtpForm(false);
    setOtp(new Array(6).fill(""));
  };
  const goBackToClientSelection = () => {
    setShowLoginForm(false);
    setShowClientDropdown(true);
    setSelectedClient(null);
    setEmail("");
    setProviders([]);
    localStorage.removeItem("clientId");
    localStorage.removeItem("providerId");
  };
  return (
    <div className="login-container">
      <div className="login-left">
        <img src="/auditzenlogo-login.png" alt="Audit Illustration" className="audit-illustration" />
      </div>
      <div className="login-right">
        <div className="login-box">
          <h2>Welcome to Auditzen!!! 👋</h2>
          <p>Sign in to your account and start the adventure</p>
          {showClientDropdown && (
            <>
              <select onChange={(e) => handleClientSelect(e.target.value)} defaultValue="" className="client-select" disabled={isFetchingClientDetails}>
                <option value="" disabled>Select your client</option>
                {clients.map((client) => (
                  <option key={client.clientId} value={client.clientId}>{client.clientName}</option>
                ))}
              </select>
            </>
          )}
          {showLoginForm && selectedClient && !showOtpForm && (
            <>
              {isDevOtpAllowed && (
                <>
                  <input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="login-input" disabled={loading} onKeyPress={(e) => { if (e.key === 'Enter') handleEmailLogin(); }} />
                  <button className={`login-button ${isButtonClicked ? "clicked" : ""}`} disabled={isButtonClicked || loading} onClick={handleEmailLogin}>{loading ? "Sending OTP..." : "Send OTP"}</button>
                  <div className="divider">OR</div>
                </>
              )}
              {providers.length > 0 && (
                <>
                  {providers.map((provider) => (
                    <button key={provider.providerId} onClick={() => handleProviderLogin(provider.providerId, provider.providerName)} className="provider-button" disabled={loading}>
                      <span className="provider-icon"> {getProviderIcon(provider.providerName)}</span>
                      <span className="providerIcon"> Sign in with {provider.providerName} {provider.providerName.toLowerCase().includes("saml") && " (SAML)"}</span>
                    </button>
                  ))}
                </>
              )}
              <button onClick={goBackToClientSelection} className="login-button" disabled={loading}>← Back to Client Selection</button>
            </>
          )}
          {showOtpForm && isDevOtpAllowed && (
            <>
              <div className="otp-header">
                <h3>Enter OTP</h3>
                <p>We sent a 6-digit code to {email}</p>
              </div>
              <div className="otp-container">
                {otp.map((digit, index) => (
                  <input key={index} ref={(el) => (otpRefs.current[index] = el)} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={1} value={digit} onChange={(e) => handleOtpChange(index, e.target.value)} onKeyDown={(e) => handleKeyDown(index, e)} onPaste={handlePaste} className="otp-box" disabled={loading} autoFocus={index === 0} />
                ))}
              </div>
              <div className="button-container">
                <button className="login-button" disabled={loading || otp.join('').length !== 6} onClick={handleOtpVerification} > {loading ? "Verifying..." : "Verify OTP"}</button>
              </div>
              <button onClick={goBackToEmail} className="login-button">Back</button>
              <div className="resend-otp">
                <p>Didn't receive the code?{" "}
                  {resendTimer > 0 ? (
                    <span>Resend in {resendTimer}s</span>
                  ) : (
                    <button onClick={handleResendOtp} className="resend-button" disabled={loading}> Resend OTP </button>
                  )}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default Login;